import type { EpisodeItem } from "@/mocks/episodes";
import { padNo } from "@/utils/anime";

interface EpisodeListProps {
  episodes: EpisodeItem[];
  selected: number;
  onSelect: (no: number) => void;
  currentEp?: EpisodeItem;
  totalEpisodes: number;
}

export default function EpisodeList({ episodes, selected, onSelect, currentEp, totalEpisodes }: EpisodeListProps) {
  return (
    <section aria-labelledby="episode-title" className="rounded-lg border border-background-200 bg-background-100">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 md:px-5 py-4 border-b border-background-200">
        <h2 id="episode-title" className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
          <i className="ri-play-list-2-line text-accent-500" />
          播放选集
        </h2>
        <span className="px-2 py-1 rounded-md bg-accent-100 text-accent-900 text-[11px] flex items-center gap-1 whitespace-nowrap">
          <i className="ri-error-warning-line" />
          演示界面 · 暂不提供真实播放
        </span>
      </div>

      <div className="px-4 md:px-5 py-4">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-full sm:w-[210px] shrink-0">
            <div className="relative w-full aspect-video rounded-md overflow-hidden bg-primary-950">
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3">
                <span className="w-10 h-10 rounded-full bg-background-50/15 text-background-50 flex items-center justify-center">
                  <i className="ri-play-fill text-xl" />
                </span>
                <p className="mt-2 text-[11px] text-background-200 leading-relaxed">
                  视频播放为演示占位
                  <br />
                  暂无真实片源
                </p>
              </div>
              <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-primary-950/80 text-background-100 text-[10px]">
                EP {padNo(selected)}
              </span>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[11px] text-foreground-500">
              第 {selected} 集 / 共 {totalEpisodes} 集{totalEpisodes > episodes.length ? "（此处展示前 24 集）" : ""}
            </p>
            <h3 className="mt-1 font-heading text-[15px] font-semibold text-foreground-950">
              {currentEp ? currentEp.title : "暂无集数信息"}
            </h3>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-foreground-600">
              {currentEp ? currentEp.synopsis : "这部作品的集数信息还在整理中，可以先看看相关推荐。"}
            </p>
            <div className="mt-2 flex items-center gap-3 text-[11px] text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {currentEp?.duration || "--"}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {currentEp?.airDate || "--"}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
          {episodes.map((ep) => {
            const active = ep.no === selected;
            return (
              <button
                key={ep.no}
                type="button"
                onClick={() => onSelect(ep.no)}
                aria-label={`第 ${ep.no} 集 ${ep.title}`}
                aria-current={active ? "true" : undefined}
                className={`h-9 rounded-md text-[12px] transition-colors cursor-pointer whitespace-nowrap ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {padNo(ep.no)}
              </button>
            );
          })}
        </div>

        <p className="mt-3 text-[11px] text-foreground-400 flex items-start gap-1 leading-relaxed">
          <i className="ri-information-line mt-0.5" />
          说明：以上为演示选集界面，点击会切换该集简介，但不会加载任何视频内容。
        </p>
      </div>
    </section>
  );
}